<template>
  <Dialog v-model:visible="displayDialogRef" class="image-upload__dialog" :class="imgSrc ? 'has-image' : null"
    :header="t('ProfilePage.ProfilePicture.Header')" @hide="resetImage()" @show="getProfileConfiguration"
    :draggable="false" :pt="{
      content: {
        style: {
          padding: '2rem',
        }
      }
    }">
    <div class="profile-picture-visibility-notice-container">
      <div class="image-container">
        <p v-html="getContent">
        </p>
        <vue-cropper v-show="imgSrc" ref="cropper" class="cropper" :src="imgSrc" :min-crop-box-width="198"
          :min-crop-box-height="257" :min-canvas-width="250" :min-canvas-height="350" :min-container-width="200"
          :min-container-height="300" :aspect-ratio="0.8078" :height="400" />
      </div>
      <div class="flex flex-col gap-2 mb-4">
        <div class="pt-3">
          <div class="p-field-checkbox">
            <Checkbox id="isPublicProfileViewable" v-model="profil.profilbildAnzeigen" :binary="true" />

            <label for="isPublicProfileViewable">{{ t('ProfilePage.PublicProfile.ShowProfilePictureInPublicProfile') }}
              <IconInfo class="cursor-pointer info-icon fill-primary" @click="toggleVisibilityNotice"
                @mouseover="isProfilePictureVisiblityNoticeVisible = true"
                @mouseleave="isProfilePictureVisiblityNoticeVisible = false" />
            </label>
            <profile-picture-visibility-notice v-if="isProfilePictureVisiblityNoticeVisible"
              @closed-visibility-notice="toggleVisibilityNotice" @clicked-router-link="emit('resetDisplay')" />
          </div>
        </div>
        <div class="p-py-2">
          <div class="flex items-start p-field-checkbox">
            <Checkbox id="is3ApplicationUse" v-model="profil.drittSystemeprofilBildAnzeigen" :binary="true" />
            <label for="is3ApplicationUse">
              {{ t('ProfilePage.PublicProfile.Allow3ApplicationUse') }}
              <IconInfo class="cursor-pointer info-icon fill-primary" @click="toggleVisibilityNotice"
                @mouseover="isProfilePictureVisiblityNoticeVisible = true"
                @mouseleave="isProfilePictureVisiblityNoticeVisible = false" />
            </label>
          </div>
        </div>
      </div>

      <div class="flex flex-col justify-end gap-2 lg:flex-row button-container">
        <label for="file-upload"
          class="flex items-center !w-full lg:!w-fit !justify-center px-4 py-2 text-base text-white rounded-lg cursor-pointer lg:w-autoflex"
          :class="imgSrc ? 'bg-primary-400' : 'bg-primary-500'">
          {{ imgSrc ? 'Profilbild ändern' : 'Profilbild auswählen' }}
        </label>
        <input id="file-upload" ref="input" type="file" name="image" accept="image/*" @change="setImage">
        <ButtonStd class="!w-full lg:!w-fit p-4 text-base border-none rounded-lg cursor-pointer p-button-primary"
          label="Profilbild hochladen" :loading="loading" :disabled="!imgSrc" @click="uploadImage">

        </ButtonStd>
      </div>
    </div>
  </Dialog>
</template>

<script setup lang="ts">
import 'cropperjs/dist/cropper.css';

import type { OeffentlichesProfilKonfigurationViewModel } from "@/types/contracts/generated/models/oeffentlichesProfilKonfigurationViewModel";

import { ref, watch } from 'vue'
import Dialog from 'primevue/dialog'
import Checkbox from 'primevue/checkbox'
// eslint-disable-next-line
import VueCropper from 'vue-cropperjs'
import ProfilePictureVisibilityNotice from './ProfilePictureVisibilityNotice.vue'
import { useBcwServices } from "@/composables/http/use-bcw-services";
import { useI18n } from "vue-i18n";
import ButtonStd from '@/components/buttons/ButtonStd.vue';
import IconInfo from '@/components/icons/IconInfo.vue';
import { useToast } from 'primevue/usetoast';


const { t } = useI18n();

const props = defineProps<{
  displayDialog: boolean;
}>();

const emit = defineEmits<{
  resetDisplay: [],
}>();
const getContent = t('ProfilePage.ProfilePicture.Content')
const { postStammdatenPhoto, getOeffentlichesProfilKonfig, postOeffentlichesProfilKonfig } = useBcwServices();
const toast = useToast();
const isProfilePictureVisiblityNoticeVisible = ref(false);
const imgSrc = ref('');
const cropImg = ref('');
const profil = ref<OeffentlichesProfilKonfigurationViewModel>({});
const is3rdApplicationUsageNoticeVisible = ref(false);
const loading = ref(false);
const cropper = ref();
const displayDialogRef = ref(props.displayDialog);
watch(props, () => {
  displayDialogRef.value = props.displayDialog;
});

function setImage(e: any) {
  const file = e.target.files[0]
  if (file.type.indexOf('image/') === -1) {
    alert('Bitte wählen Sie eine Bild-Datei aus')
    return
  }
  if (file.size < 20000) {
    alert('Das von Ihnen ausgewählte Profilbild ist zu klein.')
    return
  }
  if (file.size > 5000000) {
    alert('Das von Ihnen ausgewählte Profilbild ist zu groß.')
    return
  }
  if (typeof FileReader === 'function') {
    const reader = new FileReader()
    reader.onload = (event: any) => {
      imgSrc.value = event.target.result
      // rebuild cropperjs with the updated source
      cropper.value.replace(event.target.result)
    }
    reader.readAsDataURL(file)
  } else {
    alert('Sorry, FileReader API not supported')
  }
}

function uploadImage(): void {
  // get image data for post processing, e.g. upload or setting image src
  cropImg.value = cropper.value.getCroppedCanvas().toDataURL().split(',')[1]
  const base64Length = cropImg.value.slice(cropImg.value.indexOf(',') + 1).length
  const padding = (cropImg.value.charAt(cropImg.value.length - 2) === '=')
    ? 2
    : ((cropImg.value.charAt(
      cropImg.value.length - 1) === '=')
      ? 1
      : 0)
  const fileSize = base64Length * 0.75 - padding
  // if (fileSize > 5242880) {
  //   alert(
  //     'Bitte wählen Sie ein kleineres Bild aus. Ihr hochzuladendes Bild darf eine maximale Größe von 5 MB haben')
  //   return
  // }
  loading.value = true;
  // ENDPOINT.CORE_DATA.PHOTO.POST
  postStammdatenPhoto({ base64Photo: cropImg.value }).then(() => {
    onUploadImageSuccess();
  }).catch(err => {
    onUploadImageError(err);
  });
}

function resetImage(): void {
  imgSrc.value = '';
  emit('resetDisplay');
}

function showProfilePictureVisiblityNotice(): void {
  isProfilePictureVisiblityNoticeVisible.value = true;
}

function onUploadImageSuccess() {
  // ENDPOINT.PROFIL_KONFIGURATION.OEFFENTLICHES_PROFIL.POST
  postOeffentlichesProfilKonfig(profil.value).then(() => {
    onProfileSuccess();
  }).catch(err => {
    onUploadImageError(err);
  });
}

function onProfileSuccess() {
  loading.value = false;
  toast.add({
    summary: t('ProfilePage.ProfilePicture.Success'),
    severity: "success",
    life: import.meta.env.VITE_TOAST_FIXED_LIFETIME ? import.meta.env.VITE_TOAST_FIXED_LIFETIME_VALUE : (message.sticky ? undefined : import.meta.env.VITE_TOAST_FIXED_LIFETIME_VALUE)
  });
  emit('resetDisplay');
  emit('hide')
  // store.updateProfilePicture(true)
}

function getProfileConfiguration(): void {
  loading.value = true;
  // ENDPOINT.PROFIL_KONFIGURATION.OEFFENTLICHES_PROFIL.Get
  getOeffentlichesProfilKonfig().then(data => {
    onGetProfileConfigurationSuccess(data);
  }).catch(err => {
    onGetProfileConfigurationFailure(err);
  })
}

function onGetProfileConfigurationSuccess(responses: any) {
  loading.value = false;
  profil.value = responses as OeffentlichesProfilKonfigurationViewModel;
}

function onGetProfileConfigurationFailure(e: Error) {
  loading.value = false
  console.log(e)
}

function onUploadImageError(e: Error) {
  loading.value = false
  console.log({ e })
}

function onInfoIconClick() {
  showProfilePictureVisiblityNotice()
}

function toggleVisibilityNotice() {
  isProfilePictureVisiblityNoticeVisible.value = !isProfilePictureVisiblityNoticeVisible.value
}

function toggle3rdApplicationUsageNoticeVisibility() {
  is3rdApplicationUsageNoticeVisible.value = !is3rdApplicationUsageNoticeVisible.value
}
</script>

<style lang="css" scoped>
input[type="file"] {
  display: none;
}

label {
  width: fit-content;
}

p {
  margin-bottom: 16px;
}

.cropper {
  margin-bottom: 24px;
  height: 400px;
}

.p-field-checkbox {
  display: flex;
  gap: .5rem;
}
</style>

<style lang="css" scoped>
.image-upload__dialog.p-dialog {

  &.has-image {
    height: 80vh;
  }

  .p-dialog-header {
    .p-dialog-title {}
  }

  .p-dialog-content {
    padding: 0 3rem 2rem 2rem;
  }

  .p-field-checkbox {
    position: relative;
  }

  .button-container {
    button {
      height: fit-content;
    }
  }

}
</style>
